import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, KeyRound, ArrowLeft, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';

const API_FALLBACK = 'https://viaggio-test-production.up.railway.app';
const COOLDOWN_SECONDS = 60;

export default function ResetPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [cooldown, setCooldown] = useState(0);
  const [status, setStatus] = useState({ loading: false, error: '', success: '' });

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const postRequest = async (endpoint, payload) => {
    if (api && typeof api.post === 'function') {
      try {
        const res = await api.post(endpoint, payload);
        return { ok: true, data: res.data };
      } catch (err) {
        return { 
          ok: false, 
          detail: err?.response?.data?.detail || err?.message || 'Errore di comunicazione con il server' 
        };
      }
    }
    const res = await fetch(`${API_FALLBACK}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data, detail: data?.detail || 'Errore durante la richiesta' };
  };

  const handleRequestCode = async (e) => {
    e?.preventDefault();
    const cleanEmail = email.toLowerCase().trim();
    if (!cleanEmail) {
      setStatus({ loading: false, error: 'Inserisci un indirizzo email valido', success: '' });
      return;
    }

    setStatus({ loading: true, error: '', success: '' });

    const result = await postRequest('/api/auth/forgot-password', { email: cleanEmail });

    if (result.ok) {
      setStatus({ 
        loading: false, 
        error: '', 
        success: 'Codice inviato! Controlla la tua casella di posta.' 
      });
      setCooldown(COOLDOWN_SECONDS);
      setStep(2);
    } else {
      setStatus({ loading: false, error: result.detail, success: '' });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.trim().length !== 6) {
      setStatus({ loading: false, error: 'Inserisci il codice di sicurezza a 6 cifre', success: '' });
      return;
    }

    if (password !== confirmPassword) {
      setStatus({ loading: false, error: 'Le password non coincidono', success: '' });
      return;
    }

    if (password.length < 6) {
      setStatus({ loading: false, error: 'La password deve contenere almeno 6 caratteri', success: '' });
      return;
    }

    setStatus({ loading: true, error: '', success: '' });

    const result = await postRequest('/api/auth/reset-password', {
      email: email.toLowerCase().trim(),
      code: code.trim(),
      new_password: password
    });

    if (result.ok) {
      setStatus({ 
        loading: false, 
        error: '', 
        success: 'Password aggiornata con successo! Reindirizzamento al login...' 
      });
      setTimeout(() => navigate('/login'), 2500);
    } else {
      setStatus({ loading: false, error: result.detail, success: '' });
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1437] text-white flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#12204f]/60 border border-gray-800 rounded-2xl p-8 shadow-xl">
        
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F1D26B] via-[#D4AF37] to-[#7A5A18] mx-auto flex items-center justify-center mb-3 shadow-[0_0_18px_rgba(212,175,55,0.35)]">
            <KeyRound className="w-6 h-6 text-[#0B1437]" />
          </div>
          <h2 className="text-2xl font-serif text-[#ede7d8] font-bold">
            {step === 1 ? 'Recupera Password' : 'Crea Nuova Password'}
          </h2>
          <p className="text-xs text-gray-300 mt-1">
            {step === 1 
              ? 'Inserisci la tua email per ricevere il codice di sicurezza a 6 cifre.' 
              : 'Inserisci il codice ricevuto e la nuova password.'}
          </p>
        </div>

        {status.error && (
          <div className="text-xs text-red-400 bg-red-950/50 border border-red-900/60 p-3 rounded-xl text-center mb-4">
            {status.error}
          </div>
        )}

        {status.success && (
          <div className="text-xs text-emerald-400 bg-emerald-950/50 border border-emerald-900/60 p-3 rounded-xl text-center mb-4">
            {status.success}
          </div>
        )}

        {step === 1 ? (
          /* STEP 1: Richiesta Email */
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                La tua Email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.com"
                  className="w-full bg-[#0b1437] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#d4af37]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status.loading}
              className="w-full py-3 bg-[#d4af37] hover:bg-[#b8962d] text-[#0b1437] font-bold rounded-xl text-sm transition shadow-lg cursor-pointer disabled:opacity-50"
            >
              {status.loading ? 'Invio in corso...' : 'Invia Codice di Recupero'}
            </button>
          </form>
        ) : (
          /* STEP 2 */
          <div className="space-y-4">
            
            {/* Blocco Codice OTP: FUORI dal form così Chrome non lo associa come credenziale */}
            <div>
              <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                Codice a 6 Cifre Ricevuto
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full bg-[#0b1437] border border-[#d4af37]/60 rounded-xl px-4 py-2.5 text-center text-lg tracking-[0.3em] font-bold text-[#F1D26B] focus:outline-none focus:border-[#d4af37]"
              />
            </div>

            {/* Form dedicato solo a Username e Nuova Password per il Password Manager */}
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                  Email Account
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                  <input
                    type="email"
                    name="username"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#0b1437] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#d4af37]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                  Nuova Password (min. 6 caratteri)
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                  <input
                    type="password"
                    name="new-password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-[#0b1437] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#d4af37]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
                  Conferma Nuova Password
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
                  <input
                    type="password"
                    name="confirm-new-password"
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-[#0b1437] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#d4af37]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status.loading}
                className="w-full py-3 bg-[#d4af37] hover:bg-[#b8962d] text-[#0b1437] font-bold rounded-xl text-sm transition shadow-lg cursor-pointer disabled:opacity-50"
              >
                {status.loading ? 'Salvataggio...' : 'Reimposta Password'}
              </button>
            </form>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={handleRequestCode}
                disabled={cooldown > 0 || status.loading}
                className="text-xs text-[#D4AF37] hover:text-[#F1D26B] transition disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>
                  {cooldown > 0 ? `Rimanda codice tra ${cooldown}s` : 'Non hai ricevuto il codice? Rimandalo'}
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center border-t border-gray-800 pt-4">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white transition">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Torna al Login</span>
          </Link>
        </div>

      </div>
    </div>
  );
}
